import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'config/hooks';
import { doGetICS2Codelist } from 'store/codelists/actions';
import { SelectOption } from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { listUnLocodeExtended } from 'store/codelists/client';

const useICS2Codelists = () => {
    const dispatch = useAppDispatch();
    const ics2Codelists = useAppSelector((state) => state.codelists.ics2Codelists);
    const inFlightICS2Requests = useRef<Record<string, Promise<SelectOption[] | undefined>>>({});

    const getICS2Codelist = useCallback(
        async (
            codelistName: string | undefined,
            query?: string | undefined,
            countryCode?: string
        ): Promise<SelectOption[] | undefined> => {
            if (!codelistName) throw new Error('Missing codelist name');

            // This is a big codelist — fetch by query only, results are not stored in redux
            if (codelistName === ICS2CodelistsMap.CL244) {
                const requestKey = `${codelistName}:${countryCode ?? ''}:${query ?? ''}`;

                // Prevent multiple requests for the same query at the same time (e.g. the seed
                // request and the one fired when the dropdown is opened on a pre-filled field)
                const existingRequest = inFlightICS2Requests.current[requestKey];
                if (existingRequest) return existingRequest;

                const request = listUnLocodeExtended({
                    query,
                    countryCode,
                    page: 0,
                    size: 100,
                })
                    .then((response) =>
                        (response.list ?? []).map((code) => ({ id: code.code, value: code.description }))
                    )
                    .finally(() => {
                        delete inFlightICS2Requests.current[requestKey];
                    });

                inFlightICS2Requests.current[requestKey] = request;
                return request;
            }
            // Get the whole codelist and store it in redux
            else {
                const cached = ics2Codelists?.[codelistName];
                if (cached !== undefined) return cached;

                // Prevent multiple requests for the same codelist at the same time
                const existingRequest = inFlightICS2Requests.current[codelistName];
                if (existingRequest) return existingRequest;

                const request = dispatch(doGetICS2Codelist(codelistName)).finally(() => {
                    delete inFlightICS2Requests.current[codelistName];
                });

                inFlightICS2Requests.current[codelistName] = request;
                return request;
            }
        },
        [dispatch, ics2Codelists]
    );

    const _ensureICS2CodelistLoaded = useCallback(
        async (codelistName: string | undefined): Promise<SelectOption[]> => {
            if (!codelistName) return [];
            return (await getICS2Codelist(codelistName)) ?? [];
        },
        [getICS2Codelist]
    );

    const preloadICS2Codelists = useCallback(
        async (codelistNames: Array<string | undefined>) => {
            const uniqueNames = [...new Set(codelistNames.filter(Boolean))] as string[];
            await Promise.all(uniqueNames.map(_ensureICS2CodelistLoaded));
        },
        [_ensureICS2CodelistLoaded]
    );

    const getICS2CodeDescription = useCallback(
        (code: string | undefined | null | unknown, codelistName: string | undefined, hideKey = false): string => {
            if (!code || !codelistName || typeof code !== 'string') return '-';

            const match = ics2Codelists?.[codelistName]?.find((item) => item.id === code);

            if (!match) return hideKey ? code : code;

            return hideKey ? match.value || match.id : `${match.id}${match.value ? ` - ${match.value}` : ''}`;
        },
        [ics2Codelists]
    );

    return {
        ics2Codelists,
        getICS2Codelist,
        preloadICS2Codelists,
        getICS2CodeDescription,
    };
};

export default useICS2Codelists;
